import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import type { AnfitriaoDashboardLinks } from "@/types/anfitriao-dashboard";

import {
  CalendarDays,
  MessageCircle,
  Settings,
  BookOpen,
  Wallet,
  CheckCircle2,
  AlertTriangle,
  Edit3,
  ClipboardList,
  Stethoscope,
  Headset,
  ExternalLink,
} from "lucide-react";

const menuItems = [
  { icon: BookOpen, label: "Academy", key: "academy" },
  { icon: ClipboardList, label: "Reservas", key: "reservas" },
  { icon: CalendarDays, label: "Agenda e Disponibilidade", key: "agenda" },
  { icon: MessageCircle, label: "Mensagens", key: "mensagens" },

  { icon: BookOpen, label: "Meus Serviços", key: "servicos" },
  { icon: Wallet, label: "Financeiro", key: "financeiro" },
  { icon: CheckCircle2, label: "Avaliações", key: "avaliacoes" },

  { icon: Settings, label: "Editar Perfil", key: "perfil" },
  { icon: BookOpen, label: "Manual do Anfitrião", key: "manual" },
  { icon: Headset, label: "Tire suas dúvidas (IA)", key: "IA" },
] as const;

type ProfileItem = {
  label: string;
  filled: boolean;
};

export function ProfileSidebarAnfitriao({
  userName,
  bio,
  avatarUrl,
  perfilPct,
  cidadeEstado,
  links,
  isMv,
  profileItems = [],
  abaAtiva = "dashboard",
  onNavigate,
}: {
  userName: string;
  bio: string;
  avatarUrl?: string | null;
  perfilPct: number;
  cidadeEstado: string;
  links: AnfitriaoDashboardLinks;
  isMv: boolean;
  profileItems?: ProfileItem[];
  abaAtiva?: "dashboard" | "perfil" | "reservas" | "financeiro" | "avaliacoes" | "ia";
  onNavigate?: (aba: "dashboard" | "perfil" | "reservas" | "financeiro" | "avaliacoes" | "ia") => void;

}) {

  const [mostrarPendencias, setMostrarPendencias] = useState(false);

  const getHref = (itemKey: string | null): string => {
    return itemKey === "mensagens"
      ? "/chat/inbox"
      : itemKey === "agenda"
      ? links.agenda
      : itemKey === "reservas"
      ? "/anfitriao/dashboard?aba=reservas"
      : itemKey === "servicos"
      ? "/anfitriao/dashboard?aba=perfil&secao=servicos"
      : itemKey === "avaliacoes"
      ? "/anfitriao/dashboard?aba=avaliacoes"
      : itemKey === "academy"
      ? "/anfitriao/academy"
      : itemKey === "manual"
      ? "/manual-do-anfitriao"
      : itemKey === "IA"
      ? "/anfitriao/dashboard?aba=ia"
      : itemKey === "perfil"
      ? "/anfitriao/dashboard?aba=perfil"
      : itemKey === "financeiro"
      ? "/anfitriao/dashboard?aba=financeiro"
      : "/anfitriao/dashboard";
  };

  const itensPerfil =
    profileItems.length > 0
      ? profileItems
      : [
          { label: "Foto", filled: !!avatarUrl },
          { label: "Bio", filled: !!bio?.trim() },
          { label: "Perfil", filled: perfilPct >= 100 },
        ];

  const authUser = (window as any).__AUTH_USER__;
  const hasOng = !!authUser?.has_ong;
  const ongPanelUrl = authUser?.ong_panel_url || "/adocao/minha-ong";
  const publicProfileUrl = authUser?.public_profile_url;
  const itensFaltantes = itensPerfil.filter((item) => !item.filled);
  const perfilCompleto = itensFaltantes.length === 0;

  return (
    <div className="space-y-4">
      <Card className="card-shadow border-0 overflow-hidden">
        <div className="gradient-primary h-24 sm:h-20" />
        <CardContent className="relative pt-0 pb-5 px-5 -mt-10 text-center">
          <Avatar className="h-24 w-24 sm:h-20 sm:w-20 mx-auto border-4 border-card shadow-md overflow-hidden">
            <AvatarImage
              src={avatarUrl || undefined}
              alt={userName}
              className="object-cover object-center w-full h-full"
            />
            <AvatarFallback className="bg-secondary text-secondary-foreground text-xl font-bold">
              {userName.charAt(0)}
            </AvatarFallback>
          </Avatar>

          <h3 className="mt-3 text-lg font-bold text-foreground">{userName}</h3>
          <p className="text-xs text-muted-foreground">{cidadeEstado}</p>
          <p className="mt-2 text-[15px] leading-6 sm:text-sm text-muted-foreground">
            {bio?.trim() || "Complete seu perfil para atrair mais tutores."}
          </p>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3 gap-1.5"
            onClick={() => {
              if (onNavigate) {
                onNavigate("perfil");
                return;
              }

              window.location.href = "/anfitriao/dashboard?aba=perfil";
            }}
          >
            <Edit3 className="h-3.5 w-3.5" />
            Editar Perfil
          </Button>

          <div className="mt-5 text-left">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-foreground">
                Progresso do perfil: {perfilPct}%
              </span>

              {!perfilCompleto && (
                <span className="text-xs font-medium text-amber-700">
                  Atenção
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={() => {
                if (!perfilCompleto) {
                  setMostrarPendencias((v) => !v);
                }
              }}
              className="w-full text-left"
            >
              <Progress
                value={perfilPct}
                className={`h-3 bg-muted ${
                  perfilCompleto
                    ? "[&>div]:bg-green-600"
                    : "[&>div]:bg-amber-500"
                }`}
              />
            </button>

            {!perfilCompleto && (
              <p className="mt-2 text-xs text-muted-foreground">
                Toque na barra para ver o que falta preencher.
              </p>
            )}

            {!perfilCompleto && mostrarPendencias && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-semibold text-amber-800 mb-2">
                  Itens pendentes
                </p>

                <div className="flex flex-wrap gap-1.5">
                  {itensFaltantes.map((item) => (
                    <span
                      key={item.label}
                      className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md bg-white border border-amber-200 text-amber-800"
                    >
                      <AlertTriangle className="h-3 w-3" />
                      {item.label}
                    </span>
                  ))}
                </div>

                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => {
                      window.location.href = "/anfitriao/dashboard?aba=perfil";
                    }}
                    className="text-sm font-semibold text-coral hover:underline"
                  >
                    Completar perfil
                  </button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="card-shadow border-0">
        <CardContent className="p-2">
          <nav className="space-y-0.5">
            {publicProfileUrl && (
              <a
                href={publicProfileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-3 py-3 sm:py-2.5 rounded-lg text-base sm:text-sm font-medium text-foreground hover:bg-forest-light hover:text-forest transition-colors"
              >
                <ExternalLink className="h-4 w-4 text-muted-foreground" />
                Minha página pública
              </a>
            )}

            {menuItems.map((item) => {
              const itemKey = "key" in item ? item.key : null;

              const href: string =
                "href" in item
                  ? String((item as { href?: string }).href || "/anfitriao/dashboard")
                  : getHref(itemKey);

              const isLocalTab = itemKey === "perfil" || itemKey === "financeiro" || itemKey === "avaliacoes";

              return isLocalTab && onNavigate ? (
                <button
                  key={item.label}
                  type="button"
                  onClick={() =>
                    onNavigate?.(
                      itemKey === "financeiro"
                        ? "financeiro"
                        : itemKey === "avaliacoes"
                        ? "avaliacoes"
                        : "perfil"
                    )
                  }
                  className={`w-full flex items-center gap-3 px-3 py-3 sm:py-2.5 rounded-lg text-base sm:text-sm font-medium transition-colors text-left ${
                    abaAtiva === itemKey
                      ? "bg-forest-light text-forest"
                      : "text-foreground hover:bg-forest-light hover:text-forest"
                  }`}
                >
                  <item.icon className="h-4 w-4 text-muted-foreground" />
                  {item.label}
                </button>
              ) : (
                <a
                  key={item.label}
                  href={href}
                  className="flex items-center gap-3 px-3 py-3 sm:py-2.5 rounded-lg text-base sm:text-sm font-medium text-foreground hover:bg-forest-light hover:text-forest transition-colors"
                >
                  <item.icon className="h-4 w-4 text-muted-foreground" />
                  {item.label}
                </a>
              );
            })}

            {hasOng && (
              <a
                href={ongPanelUrl}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-forest-light hover:text-forest transition-colors"
              >
                <ClipboardList className="h-4 w-4 text-muted-foreground" />
                Painel da ONG
              </a>
            )}

            {isMv && (
              <a
                href="/vet/pacientes"
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-forest-light hover:text-forest transition-colors"
              >
                <Stethoscope className="h-4 w-4 text-muted-foreground" />
                Clínica Online
              </a>
            )}
          </nav>
        </CardContent>
      </Card>

      <Card className="card-shadow border-0">
        <CardContent className="p-2">
          <p className="px-3 pb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            Bem-estar Animal
          </p>

          <nav className="space-y-0.5">
            <a
              href="/adocao/create"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-forest-light hover:text-forest transition-colors"
            >
              <ClipboardList className="h-4 w-4 text-muted-foreground" />
              Doar Pet
            </a>

            <a
              href="/adocao"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-forest-light hover:text-forest transition-colors"
            >
              <ClipboardList className="h-4 w-4 text-muted-foreground" />
              Adotar Pet
            </a>
          </nav>
        </CardContent>
      </Card>
    </div>
  );
}
